<script setup lang="ts">
// An output's form (EditDrawer): SDI or NDI playout of an input, an encode
// or a destination (DECISIONS 2026-11-05), a player's
// HDMI screen, or some of an input's channels to a sound card or as an
// AES67 stream (DECISIONS 2026-10-06 "audio outputs").
import { computed, reactive, ref, watch } from 'vue'
import { api, type EditTarget, type Output } from '../../api'
import { aes67AddressError, freeAES67Group, ndiNameError, newId, slug } from '../../util'
import ChannelSelect from '../ChannelSelect.vue'
import PickSelect from '../PickSelect.vue'
import { clone, useDeckLink, useEditContext } from './context'

const props = defineProps<{ target: EditTarget }>()
const t = props.target
const { node, cfg, isNew, ipv4 } = useEditContext(t)
// the target's id, not output.id: output's initialiser below reads taken
// (a new output's default kind), and output was not there yet: "+ output"
// opened an empty form on a node with a free DeckLink output
const { taken, decklinkOut } = useDeckLink(() => ({ kind: 'output', id: t.kind === 'output' ? (t.id ?? '') : '' }))
const output = reactive<Output>(
    t.kind === 'output'
        ? clone(
              cfg.value.outputs?.find((o) => o.id === t.id) ?? {
                  id: '',
                  name: '',
                  input_id: t.inputId ?? cfg.value.inputs[0]?.id ?? '',
                  // SDI when a DeckLink port is free, else NDI
                  kind:
                      cfg.value.mode === 'player'
                          ? 'hdmi'
                          : (node.status?.machine.decklink ?? []).some((d) => d.output && !taken.value.has(d.name))
                            ? 'decklink'
                            : 'ndi',
                  device: '',
                  enabled: true,
              },
          )
        : { id: '', name: '', input_id: '', kind: 'decklink', enabled: true },
)
// SMPTE 2110 (DECISIONS 2026-10-10 "SMPTE 2110"): a DeckLink IP card's
// device sends its flows to these addresses; their SDPs are the card's,
// to copy into the receivers
const ipDevice = computed(() => node.status?.machine.decklink_ip?.find((d) => d.device === output.device))
const flowFields = [
    { key: 'video' as const, label: 'Video (2110-20) to' },
    { key: 'audio' as const, label: 'Audio (2110-30) to' },
    { key: 'anc' as const, label: 'Ancillary data (2110-40) to' },
]
watch(
    ipDevice,
    (d) => {
        if (d && !output.st2110) output.st2110 = { video: '' }
    },
    { immediate: true },
)
const sends = (type: string) => ipDevice.value?.flows.some((f) => f.direction === 'out' && f.type === type) ?? false
const sdp = reactive<Record<string, string>>({})
const sdpErr = ref('')
async function showSDP(type: string) {
    sdpErr.value = ''
    try {
        sdp[type] = await api.outputSDP(output.id, type)
    } catch (e) {
        sdpErr.value = e instanceof Error ? e.message : String(e)
    }
}
// the hdmi form's selects use '' for "automatic"
output.device ??= ''
// a new SDI output starts on the first free port: the select showed it while
// the form held "", and saving failed with "device is required"
if (t.kind === 'output' && output.kind === 'decklink' && !output.device)
    output.device = (node.status?.machine.decklink ?? []).find((d) => d.output && !taken.value.has(d.name))?.name ?? ''
output.display_mode ??= ''
output.audio_device ??= ''
output.on_loss ??= ''
// a player's screens and sound outputs, as the node found them
const displays = computed(() => node.status?.machine.displays ?? [])
const audioDevices = computed(() => node.status?.machine.audio_devices ?? [])
const displayModes = computed(
    () =>
        (output.device
            ? displays.value.find((d) => d.connector === output.device)
            : displays.value.find((d) => d.connected)
        )?.modes ?? [],
)
// sound only: a sound card's or an AES67 stream's
const isAudioOut = computed(() => output.kind === 'alsa' || output.kind === 'aes67')
// what it plays, as a multiview tile picks it: "input:<id>", or on SDI and
// NDI an encode or a destination (its source, DECISIONS 2026-11-05)
const plays = computed({
    get: () => output.source || (output.input_id ? 'input:' + output.input_id : ''),
    set: (v: string) => {
        const [kind, id] = v.split(':') as [string, string]
        output.input_id = kind === 'input' ? id : ''
        output.source = kind === 'input' ? undefined : v
    },
})
const destLabel = (d: { name: string; encode_id: string; delay_seconds?: number }) =>
    `${cfg.value.encodes.find((e) => e.id === d.encode_id)?.name ?? d.encode_id} › ${d.name}` +
    (d.delay_seconds ? ` (${d.delay_seconds} s behind)` : '')
// a sound output plays an input's channels only
watch(isAudioOut, (a) => {
    if (a && output.source) plays.value = 'input:' + (cfg.value.inputs[0]?.id ?? '')
})
const soundOutputs = computed(() => node.status?.machine.sound_outputs ?? [])
// the input's channels it carries, in order (1-based), each picked
const chans = ref<number[]>(output.audio_channels?.length ? [...output.audio_channels] : [1, 2])
const maxChans = computed(() => (output.kind === 'aes67' ? 8 : 16))
watch(
    () => output.kind,
    (k) => {
        // a new stream gets a free group of Dante's AES67 range, the first
        // interface: none of the node's outputs' groups, and (once the
        // announcements are read) none heard on the audio network
        if (k === 'aes67' && !output.aes67) {
            const own = (cfg.value.outputs ?? []).flatMap((o) => (o.aes67 ? [o.aes67.address] : []))
            const picked = freeAES67Group(own)
            output.aes67 = { address: picked, interface: ipv4.value[0] ?? '' }
            api.aes67Streams()
                .then((r) => {
                    const heard = r.streams.map((s) => s.group)
                    // only if it is still the pick and taken: a typed one stays
                    if (output.aes67?.address === picked && heard.includes(picked))
                        output.aes67.address = freeAES67Group([...own, ...heard])
                })
                .catch(() => {})
        }
        if (k === 'alsa' && !output.device) output.device = soundOutputs.value[0]?.device ?? ''
    },
    { immediate: true },
)
const ptp = computed(() => node.status?.ptp)
// what the node's NDI outputs appear under (noded strips brackets from the node's name)
const ndiMachine = computed(() => cfg.value.node.name.replace(/[()]/g, ''))
async function save() {
    const id = output.id || newId(slug(output.name) + '-', new Set((cfg.value.outputs ?? []).map((o) => o.id)))
    const hdmi = output.kind === 'hdmi'
    await node.save('outputs', id, {
        ...output,
        id,
        device:
            output.kind === 'decklink' || output.kind === 'alsa' || (hdmi && output.device) ? output.device : undefined,
        audio_channels: isAudioOut.value ? chans.value : undefined,
        aes67:
            output.kind === 'aes67' && output.aes67
                ? { ...output.aes67, port: Number(output.aes67.port) || undefined }
                : undefined,
        ndi_name: (output.kind === 'ndi' && output.ndi_name?.trim()) || undefined,
        display_mode: (hdmi && output.display_mode?.trim()) || undefined,
        audio_device: (hdmi && output.audio_device) || undefined,
        on_loss: (hdmi && output.on_loss) || undefined,
        slate_text: (hdmi && output.on_loss === 'slate' && output.slate_text?.trim()) || undefined,
        advanced: (hdmi && output.advanced?.trim()) || undefined,
        buffer_ms: (output.kind === 'decklink' && Number(output.buffer_ms)) || undefined,
        st2110:
            output.kind === 'decklink' && ipDevice.value && output.st2110?.video.trim()
                ? {
                      video: output.st2110.video.trim(),
                      audio: output.st2110.audio?.trim() || undefined,
                      anc: output.st2110.anc?.trim() || undefined,
                  }
                : undefined,
    })
    node.select({ kind: 'output', id })
}
// a player's output is its screen
const noun = computed(() => (output.kind === 'hdmi' ? 'screen' : 'output'))
// what the node would refuse, said before Save (WEB-37)
const addressProblem = computed(() =>
    output.kind === 'aes67' && output.aes67 ? aes67AddressError(output.aes67.address ?? '') : '',
)
const ndiProblem = computed(() => (output.kind === 'ndi' ? ndiNameError(output.ndi_name ?? '') : ''))
defineExpose({
    save,
    snapshot: () => JSON.stringify([output, chans.value]),
    noun,
    problem: () => addressProblem.value || ndiProblem.value,
})
</script>

<template>
    <template v-if="output.kind === 'hdmi'">
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Name</label><input v-model="output.name" required autofocus placeholder="TV · lobby" />
            </div>
            <div class="mb-2.5">
                <label>On screen</label
                ><select v-model="output.input_id">
                    <option value="">nothing (black)</option>
                    <option v-for="i in cfg.inputs" :key="i.id" :value="i.id">{{ i.name }}</option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>HDMI port</label
                ><select v-model="output.device">
                    <option value="">first connected screen</option>
                    <option v-for="d in displays" :key="d.connector" :value="d.connector">
                        {{ d.connector }}{{ d.connected ? '' : ' (nothing connected)' }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Display mode</label
                ><PickSelect
                    v-model="output.display_mode"
                    :options="[...new Set(displayModes)].map((m) => ({ value: m }))"
                    empty="1080p when the screen has it, else its preferred"
                    missing="not offered by this screen now"
                />
            </div>
            <div class="mb-2.5">
                <label>Sound</label
                ><select v-model="output.audio_device">
                    <option value="">the HDMI port's own</option>
                    <option v-for="a in audioDevices" :key="a.name" :value="a.name">
                        {{ a.description || a.name }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>On signal loss</label
                ><select v-model="output.on_loss">
                    <option value="">freeze the last frame</option>
                    <option value="black">black</option>
                    <option value="slate">black with a text</option>
                </select>
            </div>
        </div>
        <div v-if="output.on_loss === 'slate'" class="mb-2.5">
            <label>Text</label><input v-model="output.slate_text" placeholder="No signal" />
        </div>
        <div class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="output.enabled" type="checkbox" class="w-auto" /> enabled</label
            >
        </div>
        <p class="text-xs text-fg-3">
            Switching what is on screen does not restart the player. For a refresh rate that matches the source (50 or
            59.94), set the display mode, e.g. 1920x1080@50. Loss shows two seconds after data stops.
        </p>
        <details>
            <summary>Advanced</summary>
            <div class="mb-2.5">
                <label>Extra mpv options</label
                ><input v-model="output.advanced" class="font-mono tabular-nums" placeholder="--hwdec=drm" />
            </div>
        </details>
    </template>
    <template v-if="output.kind !== 'hdmi'">
        <div class="mb-2.5">
            <label>Name</label
            ><input
                v-model="output.name"
                required
                autofocus
                :placeholder="
                    output.kind === 'ndi' ? 'Program to NDI' : isAudioOut ? 'Talkback to comms' : 'SDI out 2 · loop'
                "
            />
        </div>
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Plays</label
                ><select v-model="plays" data-test="output-plays">
                    <optgroup label="Inputs">
                        <option v-for="i in cfg.inputs" :key="i.id" :value="'input:' + i.id">{{ i.name }}</option>
                    </optgroup>
                    <template v-if="!isAudioOut">
                        <optgroup v-if="cfg.encodes.length" label="Encodes (decoded: what leaves the node)">
                            <option v-for="e in cfg.encodes" :key="e.id" :value="'encode:' + e.id">{{ e.name }}</option>
                        </optgroup>
                        <optgroup v-if="cfg.destinations.length" label="Destinations (at their delay; decoded)">
                            <option v-for="d in cfg.destinations" :key="d.id" :value="'destination:' + d.id">
                                {{ destLabel(d) }}
                            </option>
                        </optgroup>
                    </template>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Kind</label
                ><select v-model="output.kind" :disabled="!isNew">
                    <!-- greyed out as the input's is: no card here, or every port in use -->
                    <option value="decklink" :disabled="isNew && !decklinkOut.length">
                        SDI (DeckLink){{
                            !isNew || decklinkOut.length
                                ? ''
                                : (node.status?.machine.decklink ?? []).some((d) => d.output)
                                  ? ' (every port in use)'
                                  : ' (no card detected)'
                        }}
                    </option>
                    <option value="ndi">NDI</option>
                    <option value="alsa">Sound card</option>
                    <option value="aes67">AES67 stream</option>
                </select>
            </div>
            <div v-if="isAudioOut" class="mb-2.5">
                <label>Channels of the input, in order</label>
                <div class="flex flex-wrap items-center gap-1.5" data-test="out-channels">
                    <span v-for="(_, i) in chans" :key="i" class="flex items-center gap-0.5">
                        <ChannelSelect v-model="chans[i]!" :base="1" :aria-label="`channel ${i + 1}`" />
                        <button
                            v-if="chans.length > 1"
                            type="button"
                            class="btn-ghost px-1 text-xs"
                            :aria-label="`remove channel ${i + 1}`"
                            @click="chans.splice(i, 1)"
                        >
                            ×
                        </button>
                    </span>
                    <button
                        v-if="chans.length < maxChans"
                        type="button"
                        class="btn-ghost text-xs"
                        @click="chans.push(Math.min((chans[chans.length - 1] ?? 0) + 1, 16))"
                    >
                        + channel
                    </button>
                </div>
                <p class="mt-1 mb-0 text-xs text-fg-3">
                    e.g. 3 and 4: a caster's talkback.{{ output.kind === 'aes67' ? ' At most 8.' : '' }}
                </p>
            </div>
            <div v-if="output.kind === 'alsa'" class="mb-2.5">
                <label>Sound card</label
                ><PickSelect
                    v-model="output.device"
                    :options="soundOutputs.map((d) => ({ value: d.device, label: d.name }))"
                    data-test="sound-output"
                />
                <p class="mt-1 mb-0 text-xs text-fg-3">
                    {{
                        soundOutputs.length
                            ? 'A Dante AVIO adapter, or an interface with a Linux driver.'
                            : 'No sound card found on this node.'
                    }}
                </p>
            </div>
            <template v-if="output.kind === 'aes67' && output.aes67">
                <div class="mb-2.5">
                    <label>Interface (the audio network)</label
                    ><select v-model="output.aes67.interface">
                        <option v-for="a in ipv4" :key="a" :value="a">{{ a }}</option>
                    </select>
                </div>
                <div class="mb-2.5">
                    <label>Address</label
                    ><input
                        v-model="output.aes67.address"
                        class="font-mono tabular-nums"
                        placeholder="239.69.1.2"
                        data-test="aes67-address"
                        :aria-invalid="!!addressProblem"
                    />
                    <div v-if="addressProblem" class="mt-0.5 text-xs text-bad">{{ addressProblem }}</div>
                </div>
                <div class="mb-2.5">
                    <label>Port</label
                    ><input v-model="output.aes67.port" type="number" min="1024" max="65535" placeholder="5004" />
                </div>
                <p class="col-span-2 mt-0 text-xs text-fg-3">
                    L24 at 48 kHz in 1 ms packets, announced by SAP: it shows in Dante Controller's AES67 flows and on
                    other AES67 receivers. A multicast group in 239.69.x.y is Dante's range; a receiver's own address
                    sends to it alone. Timed by the audio network's PTP grandmaster, which the node follows on this
                    interface (domain {{ cfg.ptp?.domain ?? 0 }}, in the node's settings):
                    <span v-if="ptp?.grandmaster" class="text-ok">following {{ ptp.grandmaster }}</span>
                    <span v-else-if="ptp?.error" class="text-warn">{{ ptp.error }}</span>
                    <span v-else>it starts with the first AES67 output.</span>
                </p>
            </template>
            <div v-if="output.kind === 'ndi'" class="col-span-2 mb-2.5">
                <label>NDI name</label
                ><input
                    v-model="output.ndi_name"
                    :placeholder="output.name || 'Program'"
                    :aria-invalid="!!ndiProblem"
                />
                <div v-if="ndiProblem" class="mt-0.5 text-xs text-bad">{{ ndiProblem }}</div>
                <p class="mt-1 mb-0 text-xs text-fg-3">
                    Appears on the network as “{{ ndiMachine }} ({{ output.ndi_name || output.name || 'Program' }})”,
                    full-bandwidth NDI with all 16 audio channels.
                </p>
                <p class="text-xs text-fg-3">
                    NDI® is a registered trademark of Vizrt NDI AB ·
                    <a href="https://ndi.video/" target="_blank" rel="noopener">ndi.video</a>
                </p>
            </div>
            <div v-if="output.kind === 'decklink'" class="mb-2.5">
                <label>SDI output</label>
                <select v-model="output.device" :disabled="!decklinkOut.length">
                    <option v-for="d in decklinkOut" :key="d.name" :value="d.name">{{ d.name }}</option>
                </select>
                <p v-if="!decklinkOut.length" class="mt-1 mb-0 text-xs text-fg-3">
                    {{
                        (node.status?.machine.decklink ?? []).some((d) => d.output)
                            ? 'Every DeckLink port that can play out is already used by an input or output.'
                            : 'No DeckLink output detected on this machine.'
                    }}
                </p>
            </div>
            <div v-if="output.kind === 'decklink'" class="mb-2.5">
                <label>Buffer (ms): the delay this output adds</label
                ><input
                    v-model.number="output.buffer_ms"
                    type="number"
                    min="20"
                    max="2000"
                    step="10"
                    placeholder="100"
                />
                <p class="mt-1 mb-0 text-xs text-fg-3">
                    What the card holds before it plays. Less is less delay; too little and the picture stutters when a
                    frame comes late (the node's log then says "not enough buffered video frames"). 100 ms by default.
                </p>
            </div>
            <fieldset
                v-if="output.kind === 'decklink' && ipDevice"
                class="mb-2.5 border-0 p-0"
                data-test="st2110-output"
            >
                <legend class="mb-1 text-xs text-fg-2">
                    SMPTE 2110: where this device sends each flow (address:port, a multicast group); a flow without one
                    is not sent
                </legend>
                <template v-for="f in flowFields" :key="f.key">
                    <div v-if="sends(f.key) && output.st2110" class="mb-2">
                        <label>{{ f.label }}</label>
                        <div class="flex gap-1.5">
                            <input
                                v-model="output.st2110![f.key]"
                                class="font-mono"
                                placeholder="239.10.0.21:5004"
                                :data-test="`st2110-${f.key}`"
                            />
                            <button
                                v-if="!isNew"
                                type="button"
                                class="btn-ghost text-xs"
                                :title="'its SDP, to copy into the receivers (once the output runs)'"
                                @click="showSDP(f.key)"
                            >
                                SDP
                            </button>
                        </div>
                        <pre v-if="sdp[f.key]" class="mt-1 font-mono text-[11px] whitespace-pre-wrap select-all">{{
                            sdp[f.key]
                        }}</pre>
                    </div>
                </template>
                <p v-if="sdpErr" class="mt-0 text-xs text-bad">{{ sdpErr }}</p>
            </fieldset>
        </div>
        <div class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="output.enabled" type="checkbox" class="w-auto" /> enabled</label
            >
        </div>
        <p v-if="isAudioOut" class="text-xs text-fg-3">
            Sound only, the input's channels as the encoders hear them. It adds about 60 ms
            {{ output.kind === 'aes67' ? '(and the receiver its own latency)' : '(and the card its buffer)' }}.
        </p>
        <p v-else-if="output.source" class="text-xs text-fg-3">
            Decoded on the node once, for every output playing it, about a second behind what it sends; the encode and
            its destinations never restart for an output. A destination plays at its delay. The picture follows the
            stream's format: an SDI port needs one the card plays.
        </p>
        <p v-else class="text-xs text-fg-3">
            The input is played out unencoded, exactly as the encoders see it (after conform, slate and overlay). No
            delay is available on SDI or NDI outputs.
        </p>
    </template>
</template>
